import { getWorkspaceIdFromUrl } from '@/context/current-workspace';
import { authStore } from '@/stores/auth';
import { useStore } from '@nanostores/react';
import type { MouseEvent } from 'react';
import { useMatches } from 'react-router-dom';
import { AppBundle, AppBundleRoutes } from './app-bundles';

// URLs within apps will be of the form /workspaces/1234/journeys or
// /workspaces/1234/pipelines etc.
// This function regurns a regex for matching a URL with a given app.
function workspaceAppRegex(app: AppBundle) {
  return new RegExp(`/workspaces/(last|(\\d+))${AppBundleRoutes[app]}`, 'i');
}

export const WORKSPACE_SETTINGS_REGEX = /^\/workspaces\/(last|(\d+))\/settings/i;
export const SETTINGS_REGEX = /^\/settings/i;
export const TRIAL_EXPIRED_REGEX = /^\/trial-expired\/journeys$/;

// There are a few special URLs that don't follow the above pattern. This
// contains regexes for matching those special cases.
const SPECIAL_URLS: [RegExp, AppBundle][] = [
  [WORKSPACE_SETTINGS_REGEX, AppBundle.Journeys],
  [SETTINGS_REGEX, AppBundle.Journeys],
  [TRIAL_EXPIRED_REGEX, AppBundle.Journeys],
  [/^\/internal-tools/i, AppBundle.Journeys],
  [/^\/logout/i, AppBundle.Journeys],
];

export function getAppBundleFromUrl(url: string): AppBundle | null {
  // Check for special URLs
  const matchBySpecialUrl = SPECIAL_URLS.find(([regex]) => url.match(regex) != null);
  if (matchBySpecialUrl != null) {
    return matchBySpecialUrl[1];
  }

  return Object.values(AppBundle).find((app) => url.match(workspaceAppRegex(app)) != null) ?? null;
}

// This hook returns a function that adds the masq_account_id parameter
// to the URL/path if necessary: user is masquerading and URL doesn't identify the workspace
export function useMasqLink() {
  const auth = useStore(authStore);
  const isMasq = auth.account?.is_masq ?? false;

  return (url: string | null) => {
    if (url == null) {
      return '';
    }

    if (!isMasq) {
      return url;
    }

    const targetApp = getAppBundleFromUrl(url);
    const isWorkspaceIdentified = targetApp != null && getWorkspaceIdFromUrl(url) != null;
    if (isWorkspaceIdentified) {
      return url;
    }

    if (url.includes('masq_account_id=')) {
      return url;
    }

    return `${url}?masq_account_id=${auth.account?.id}`;
  };
}

export function shouldProcessLinkClick(event: MouseEvent) {
  const isTargetBlank = event.currentTarget instanceof HTMLAnchorElement && event.currentTarget.target === '_blank';
  return event.button === 0 && !isTargetBlank && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

export function useIsSettings() {
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];
  const isSettingsPage = lastMatch?.id === 'settings';
  const isWorkspaceSettingsPage = lastMatch?.id === 'workspace_settings';
  const isAnySettingsPage = isSettingsPage || isWorkspaceSettingsPage;

  return {
    isSettingsPage,
    isWorkspaceSettingsPage,
    isAnySettingsPage,
  };
}

export function workspaceRootUrl(workspace: { id: number } | null) {
  if (workspace == null) {
    return '';
  }

  return `/workspaces/${workspace.id}/journeys`;
}
